import { Icon } from "react-migration/components/Icon";
import type { Color } from "deck.gl";
import type { PatternMapping } from "react-migration/domains/constraints/designation/style/types";
import {
  getCategoryPatternMapping,
  getDesignationFillColour,
  getDesignationIconKeyAccessor,
  getDesignationLineColour,
} from "react-migration/domains/constraints/designation/style/accessors";
import patternConfig from "react-migration/domains/constraints/designation/style/pattern.json";
import patternImg from "react-migration/domains/constraints/designation/style/pattern.png";
import clsx from "clsx";
import { SingleDesignation } from "../../typings/applicationTypes/SingleDesignation";

type SpriteArrangement = [number, number];

function calculateMaskPosition(
  [spriteIndexX, spriteIndexY]: SpriteArrangement,
  [spritesInRow, spritesInColumn]: SpriteArrangement
) {
  const maskPosX = spritesInRow > 1 ? (spriteIndexX * 100) / (spritesInRow - 1) : 0;
  const maskPosY = spritesInColumn > 1 ? (spriteIndexY * 100) / (spritesInColumn - 1) : 0;

  return `${maskPosX}% ${maskPosY}%`;
}

function getFillPatternMaskCSS(patternMapping: PatternMapping): React.CSSProperties {
  const { arrangement: atlasArrangement } = patternConfig.meta;
  const { arrangement } = patternMapping;
  const maskImage = `url(${patternImg})`;

  const maskPosition = calculateMaskPosition(arrangement as SpriteArrangement, atlasArrangement as SpriteArrangement);

  const maskSize = `${atlasArrangement[0] * 100}% ${atlasArrangement[1] * 100}%`;

  return {
    maskImage,
    maskPosition,
    maskSize,
    WebkitMaskImage: maskImage,
    WebkitMaskPosition: maskPosition,
    WebkitMaskSize: maskSize,
    transform: "scale(1.3)",
  };
}

function getHatchAlpha(deckGlAlpha?: number) {
  const cssAlpha = (deckGlAlpha ?? 255) / 255;
  return cssAlpha && +((cssAlpha + 1) / 2).toFixed(2);
}

type SharedProps = Partial<Omit<BaseHatchProps, "id">> & {
  selectedAttribute?: string;
  fillColorOverride?: Color;
  lineColorOverride?: Color;
};

type DesignationHatchProps = SharedProps &
  (
    | {
        /** @deprecated please use `designation` instead of `id` */
        id: string;
        designation?: never;
      }
    | {
        /** @deprecated please use `designation` instead of `id` */
        id?: never;
        designation: Pick<SingleDesignation, "sub_category_id" | "designation_attributes">;
      }
  );

/**
 * Was initially implemented to use `id`, updating to use `designation` instead.
 *
 * @todo Remove `id` prop in a future PR once all consumers have been updated to
 * use `designation`.
 */
export const DesignationHatch = ({
  id,
  designation: _designation,
  fillColorOverride,
  lineColorOverride,
  selectedAttribute,
  ...rest
}: DesignationHatchProps) => {
  // TODO Compatibility with old `id` prop, remove in a future PR.
  const designation = _designation ?? {
    sub_category_id: id,
    designation_attributes: [],
  };

  const fillColor = fillColorOverride ?? getDesignationFillColour(designation, selectedAttribute);
  const backgroundColor = `rgba(${fillColor.slice(0, 3)}, ${getHatchAlpha(fillColor[3])})`;

  const borderColor = `rgb(${(
    lineColorOverride ??
    fillColorOverride ??
    getDesignationLineColour(designation, selectedAttribute)
  ).slice(0, 3)})`;

  const patternMapping = getCategoryPatternMapping(designation.sub_category_id);
  const hasPattern = !!patternMapping;
  const iconKey = getDesignationIconKeyAccessor(designation);
  const hasIcon = !!iconKey;

  return hasIcon ? (
    <span style={{ color: backgroundColor }}>
      <Icon icon={`icon-${iconKey}`} extraClasses="align-top" />
    </span>
  ) : (
    <Hatch
      {...rest}
      fillColor={backgroundColor}
      borderColor={borderColor}
      borderWidth={hasPattern ? HatchBorderWidth.ONE : HatchBorderWidth.TWO}
      extraStyle={hasPattern ? getFillPatternMaskCSS(patternMapping) : undefined}
    />
  );
};

enum HatchBorderWidth {
  ONE = "1",
  TWO = "2",
}

interface BaseHatchProps {
  fillColor?: string;
  borderColor?: string;
  borderWidth?: `${HatchBorderWidth}`;
  extraStyle?: React.CSSProperties;
  disabled?: boolean;
}

export const Hatch = ({
  fillColor = "white",
  extraStyle,
  borderColor,
  borderWidth = HatchBorderWidth.TWO,
  disabled,
}: BaseHatchProps) => {
  return (
    <div
      data-testid="hatch"
      style={{ borderColor: borderColor || fillColor }}
      className={clsx(
        "h-5 w-5  overflow-hidden shrink-0 grow-0 shadow transition-all duration-200",
        borderWidth === HatchBorderWidth.ONE ? "border" : "border-2",
        "h-5 w-5 rounded-sm",
        disabled && "opacity-25 grayscale"
      )}
    >
      <div
        className="w-full h-full"
        style={{
          backgroundColor: fillColor,
          ...extraStyle,
        }}
      />
    </div>
  );
};
